<script setup lang="ts">
import { computed } from 'vue'

import type {
  MetaDateRange,
  MetaDraftValue,
  WatchlistFormField,
} from '../../models/watchlist.types'

import { useI18n } from '@/shared/i18n'
import {
  DateInput,
  Input,
  MultiSelect,
  Select,
  Switch,
  TagInput,
  type DateInputRange,
  type DateInputValue,
} from '@/shared/ui'
import type { MonthLabels, WeekdayLabels } from '@/shared/ui/date-picker'

const props = defineProps<{ field: WatchlistFormField; value: MetaDraftValue }>()
const emit = defineEmits<{ change: [value: MetaDraftValue] }>()
const { t } = useI18n()
const label = computed(
  () => `${props.field.placeholder || props.field.label}${props.field.required ? ' *' : ''}`,
)
const options = computed(() => props.field.options ?? [])
const weekdays = computed<WeekdayLabels>(
  () =>
    ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((day) =>
      t(`workspaceDefinitions.watchlist.meta.date.weekdays.${day}`),
    ) as unknown as WeekdayLabels,
)
const months = computed<MonthLabels>(
  () =>
    ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].map(
      (month) => t(`workspaceDefinitions.watchlist.meta.date.months.${month}`),
    ) as unknown as MonthLabels,
)

function parseDate(value: string | null): Date | null {
  if (!value) return null
  const [year, month, day] = value.split('-').map(Number)
  return year && month && day ? new Date(year, month - 1, day) : null
}

function formatDate(value: Date | null): string | null {
  if (!value) return null
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
}

const dateRange = computed<DateInputRange>(() => {
  const value =
    props.value && typeof props.value === 'object' && !Array.isArray(props.value)
      ? (props.value as MetaDateRange)
      : { start: null, end: null }
  return { start: parseDate(value.start), end: parseDate(value.end) }
})

function updateDate(value: DateInputValue): void {
  if (!value || value instanceof Date) return
  emit('change', { start: formatDate(value.start), end: formatDate(value.end) })
}
</script>

<template>
  <Input
    v-if="field.ui_component === 'text'"
    :model-value="String(value ?? '')"
    :label="label"
    size="sm"
    @update:model-value="emit('change', String($event))"
  />
  <Select
    v-else-if="field.ui_component === 'singleselect'"
    :model-value="typeof value === 'string' || typeof value === 'number' ? value : null"
    :options="options"
    :label="label"
    searchable
    clearable
    size="sm"
    @update:model-value="emit('change', $event)"
  />
  <MultiSelect
    v-else-if="field.ui_component === 'multiselect'"
    :model-value="Array.isArray(value) ? value : []"
    :options="options"
    :label="label"
    searchable
    :display-chips="false"
    size="sm"
    @update:model-value="emit('change', $event)"
  />
  <DateInput
    v-else-if="field.ui_component === 'daterange'"
    :model-value="dateRange"
    selection-mode="range"
    :weekday-labels="weekdays"
    :month-labels="months"
    :trigger-label="label"
    :calendar-label="label"
    range-separator=" – "
    :label="label"
    size="sm"
    @update:model-value="updateDate"
  />
  <Switch
    v-else-if="field.ui_component === 'toggle'"
    :model-value="Boolean(value)"
    :label="label"
    size="sm"
    @update:model-value="emit('change', $event)"
  />
  <TagInput
    v-else-if="field.ui_component === 'taginput'"
    :model-value="Array.isArray(value) ? value : []"
    :label="label"
    v-bind="field.placeholder ? { placeholder: field.placeholder } : {}"
    add-on-blur
    :add-label="t('workspaceDefinitions.watchlist.meta.action.addTag')"
    size="sm"
    @update:model-value="emit('change', $event)"
  />
</template>
